"use client";

import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { Swiper as SwiperType } from "swiper";
import { createPortal } from "react-dom";
import useProductDetail from "@/app/lib/features/productDetail/productDetail.hook";
import ThumbnailSidebar from "./more/ThumbnailSidebar";
import MainImageContainer from "./more/MainImageContainer";
import ModelInfo from "./more/ModelInfo";
import dynamic from "next/dynamic";

const ZoomModal = dynamic(() => import("./more/ZoomModal"), { ssr: false });

const ImageSlider = () => {
  const { product } = useProductDetail();
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isZoomModalOpen, setIsZoomModalOpen] = useState(false);
  const imageContainerRef = useRef<HTMLDivElement | null>(null);
  const mainSwiperRef = useRef<SwiperType | null>(null);
  const zoomSwiperRef = useRef<SwiperType | null>(null);

  const allImages = useMemo(() => {
    if (!product) return [];
    return product.images;
  }, [product]);

  const productImagesLength = product?.images.length ?? 0;

  const isCustomerImage = useCallback(
    (index: number) => index >= productImagesLength,
    [productImagesLength],
  );

  const handleImageClick = useCallback(() => {
    setIsZoomModalOpen(true);
  }, []);

  const handleZoomModalClose = useCallback(() => {
    setIsZoomModalOpen(false);
  }, []);

  const handleZoomSlideChange = useCallback((swiper: SwiperType) => {
    setActiveImageIndex(swiper.activeIndex);
    mainSwiperRef.current?.slideTo(swiper.activeIndex);
  }, []);

  const handleThumbnailClick = useCallback((index: number) => {
    setActiveImageIndex(index);
    mainSwiperRef.current?.slideTo(index);
  }, []);

  const handleSlideChange = useCallback((swiper: SwiperType) => {
    setActiveImageIndex(swiper.activeIndex);
  }, []);

  useEffect(() => {
    if (isZoomModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isZoomModalOpen]);

  if (!product) return null;

  return (
    <>
      <div className="space-y-3 flex flex-col">
        <div className="flex gap-3 relative">
          <ThumbnailSidebar
            images={allImages}
            activeIndex={activeImageIndex}
            onThumbnailClick={handleThumbnailClick}
            isCustomerImage={isCustomerImage}
            orientation="vertical"
          />
          <MainImageContainer
            ref={imageContainerRef}
            images={allImages}
            onImageClick={handleImageClick}
            onSlideChange={handleSlideChange}
            onSwiperInit={(swiper) => {
              mainSwiperRef.current = swiper;
            }}
            isCustomerImage={isCustomerImage}
          />
        </div>
        <ThumbnailSidebar
          images={allImages}
          activeIndex={activeImageIndex}
          onThumbnailClick={handleThumbnailClick}
          isCustomerImage={isCustomerImage}
          orientation="horizontal"
        />
        <ModelInfo />
      </div>
      {typeof window !== "undefined" &&
        isZoomModalOpen &&
        createPortal(
          <ZoomModal
              images={allImages}
              activeIndex={activeImageIndex}
              onClose={handleZoomModalClose}
              onSlideChange={handleZoomSlideChange}
              onSwiperInit={(swiper) => {
                zoomSwiperRef.current = swiper;
              }}
              isCustomerImage={isCustomerImage}
            />,
          document.body,
        )}
    </>
  );
};

export default ImageSlider;
